import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { MOBILE_BREAKPOINT } from '../constants/app.constants';
import { Contract } from '../models/Contract';
import { ContractParameters } from '../models/ContractParameters';
import { FormStatus, LocationType } from '../models/Enums';
import { ErrorState, ScheduleFormState } from '../models/FormState';
import { ListItem } from '../models/ListItem';
import { Location } from '../models/Location';
import { Schedule } from '../models/Schedule';
import { ScheduleDefaults } from '../models/ScheduleDefaults';
import { ContractDataService } from '../services/contract-data.service';
import { HotkeyConfig } from '../models/HotkeyConfig';
import { HOTKEY_SAVE, HOTKEY_CANCEL, KEY_ENTER } from '../models/HotkeyDefinitions';
import { HotkeyService } from '../services/hotkey.service';
import { ListDataService } from '../services/list-data.service';
import { ScheduleDataService } from '../services/schedule-data.service';


@Component({
    selector: 'schedule-form',
    templateUrl: 'schedule-form.component.html',
    styles: [],
    standalone: false
})
export class ScheduleFormComponent implements OnInit, OnChanges {
    isShowNotes: boolean;

    constructor(private _listDataService: ListDataService,
        private _scheduleDataService: ScheduleDataService,
        private _contractDataService: ContractDataService,
        private _hotkeyService: HotkeyService) {

    }

    @ViewChild('closeButton') closeButton;
    @ViewChild('locationSearchDropdown') locationSearchDropdownRef: ElementRef;
    @ViewChild('contractSearchDropdown') contractSearchDropdownRef: ElementRef;
    @Input() scheduleDefaults: ScheduleDefaults;
    @Output() saveComplete: EventEmitter<number> = new EventEmitter<number>();
    @Output() deleteComplete: EventEmitter<number> = new EventEmitter<number>();

    errorMessage: string = '';
    locationTypeName: string = '';
    loadType: string = '';
    isMobile: boolean = false;
    isNew: boolean = false;
    isLocGroup: boolean = false;

    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;

    schedule: Schedule = new Schedule()
    formState: ScheduleFormState = new ScheduleFormState();
    contractLoadsError: string = '';
    selectedContract: Contract = new Contract();

    locList: ListItem[] = [];
    commodityList: ListItem[] = [];
    contractList: Contract[] = [];
    private saveClick = new Subject<void>();

    showLocationSearch: boolean = false;
    showContractSearch: boolean = false;

    // Dropdown keyboard navigation
    locationHighlightIndex: number = -1;
    contractHighlightIndex: number = -1;

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const isLocationSearchDropdown = this.locationSearchDropdownRef?.nativeElement?.contains(target);
        const isContractSearchDropdown = this.contractSearchDropdownRef?.nativeElement?.contains(target);
        if (!isLocationSearchDropdown) {
            this.showLocationSearch = false;
            this.clearLocationSearch();
        }
        if (!isContractSearchDropdown) {
            this.showContractSearch = false;
        }
    }

    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        // Check if modal is visible
        const modal = document.getElementById('scheduleModal');
        if (!modal || !modal.classList.contains('show')) {
            return;
        }

        // Handle Enter key on checkboxes
        if (event.key === KEY_ENTER && this._hotkeyService.handleCheckboxEnter(event)) {
            return;
        }

        // Handle dropdown navigation
        if (this.showLocationSearch) {
            const handled = this._hotkeyService.handleDropdownKeyboard(event, {
                containerRef: this.locationSearchDropdownRef,
                items: this.locationList,
                highlightIndex: this.locationHighlightIndex,
                onHighlightChange: (i) => this.locationHighlightIndex = i,
                onSelect: (item) => this.selectLocation(item),
                onClose: () => { this.showLocationSearch = false; this.locationHighlightIndex = -1; }
            });
            if (handled) return;
        }

        if (this.showContractSearch) {
            const handled = this._hotkeyService.handleDropdownKeyboard(event, {
                containerRef: this.contractSearchDropdownRef,
                items: this.contractList,
                highlightIndex: this.contractHighlightIndex,
                onHighlightChange: (i) => this.contractHighlightIndex = i,
                onSelect: (item) => this.selectContract(item),
                onClose: () => { this.showContractSearch = false; this.contractHighlightIndex = -1; }
            });
            if (handled) return;
        }

        // Define hotkeys for this form
        const hotkeys: HotkeyConfig[] = [
            { ...HOTKEY_SAVE, action: () => this.onSaveHotkey() },
            { ...HOTKEY_CANCEL, action: () => this.onEscapeKey() }
        ];

        this._hotkeyService.handleHotkeys(event, hotkeys);
    }

    private onSaveHotkey(): void {
        if (this.formStatus === FormStatus.Normal) {
            this.onSaveClick();
        }
    }

    private onEscapeKey(): void {
        if (this.formStatus === FormStatus.Delete) {
            this.cancelDelete();
        } else if (this.formStatus === FormStatus.Normal) {
            this.closeButton.nativeElement.click();
        }
    }

    toggleLocationSearch() {
        this.showContractSearch = false;
        this.showLocationSearch = !this.showLocationSearch;
        this.locationHighlightIndex = -1;
        this.clearLocationSearch();
    }

    toggleContractSearch() {
        this.showLocationSearch = false;
        this.showContractSearch = !this.showContractSearch;
        this.contractHighlightIndex = -1;
    }

    get locCode(): string {
        return this.schedule.locCode;
    }
    set locCode(value: string) {
        this.schedule.locCode = value;
        this.validateLocCode();
        this.getCommodityList(value);
    }
    get commodityCode(): string {
        return this.schedule.commodityCode;
    }
    set commodityCode(value: string) {
        this.schedule.commodityCode = value;
        this.validateCommodity();
    }
    get alternateCommodity(): string {
        return this.schedule.alternateCommodity;
    }
    set alternateCommodity(value: string) {
        this.schedule.alternateCommodity = value;
        this.validateCommodity();
    }
    get scheduleBeginDate(): string {
        return this.schedule.scheduleBeginDate;
    }
    set scheduleBeginDate(value: string) {
        this.schedule.scheduleBeginDate = value;
        this.validatePeriodDates();
    }
    get scheduleEndDate() {
        return this.schedule.scheduleEndDate;
    }
    set scheduleEndDate(value: string) {
        this.schedule.scheduleEndDate = value;
        this.validatePeriodDates();
    }

    locationList: Location[] = [];
    locationSearchForm = this.createLocationSearchForm();

    createLocationSearchForm() {
        const locationSearchForm = new FormGroup({
            locationName: new FormControl(''),
            city: new FormControl(''),
            customerName: new FormControl('')
        });

        locationSearchForm.valueChanges
            .pipe(debounceTime(1000))
            .subscribe(() => {
                this.searchLocations();
            });
        return locationSearchForm;
    }

    searchLocations() {
        if (this.schedule.locCode || this.schedule.locCode.length > 0) {
            if (this.locationSearchForm.controls.locationName.value.length > 1 || this.locationSearchForm.controls.city.value.length > 1) {
                this._listDataService.searchLocations(this.locationSearchForm.getRawValue()).subscribe({
                    next: data => {
                        this.locationList = data;
                    },
                    error: err => this.errorMessage = err
                });
            }
            else {
                this.locationList = [];
            }
        }
        else {
            this.locationList = [];
            this.formState.loc.invalid = true;
            this.formState.loc.errorMessage = 'Loc is required before selecting the ' + this.locationTypeName;
        }
    }

    selectLocation(location: Location): void {
        this.schedule.location = { ...location };
        this.showLocationSearch = false;

        //default the location notes
        this._listDataService.getLocation(location.locationId, this.schedule.locCode).subscribe({
            next: data => {
                if (this.schedule.locationType == LocationType.Origin) {
                    this.schedule.locationNotes = data.originNotes;
                }
                else {
                    this.schedule.locationNotes = data.destinationNotes;
                }
            },
            error: err => this.errorMessage = err
        });

        this.validateLocation();
    }

    clearLocationSearch() {
        this.locationSearchForm = this.createLocationSearchForm();
        this.locationList = [];
    }

    getContracts() {
        this.toggleContractSearch();

        let contractParms = new ContractParameters();
        contractParms.locCode = this.schedule.locCode,
            contractParms.locationType = this.scheduleDefaults.locationType;
        contractParms.commodityCode = this.schedule.commodityCode;
        contractParms.locationId = this.schedule.location.locationId;
        contractParms.customerId = this.schedule.customer.customerId;

        this.contractList = [];
        if (contractParms.locCode.length > 0 && contractParms.commodityCode.length > 0 && contractParms.locationId > 0) {
            this._contractDataService.getOpenContracts(contractParms).subscribe({
                next: data => {
                    this.contractList = data;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    selectContract(contract: Contract) {
        this.contractLoadsError = '';
        this.selectedContract = { ...contract };
        this.selectedContract.loads = 0;
        this.showContractSearch = false;
    }

    applyContract() {
        let contractLoads = this.schedule.contracts.reduce((accumulator, contract) => accumulator + contract.loads, 0);
        contractLoads += this.selectedContract.loads;
        if (contractLoads > this.schedule.loads) {
            this.contractLoadsError = 'The total contract loads must be less than or equal to the schedule loads.';
            return;
        }

        if (this.selectedContract.contractId > 0 && this.selectedContract.loads > 0) {
            this.selectedContract.loadsDisplay = this.selectedContract.loads.toString();
            this.schedule.contracts.push({ ...this.selectedContract });
            this.selectedContract = new Contract();
            this.contractLoadsError = '';
        }
    }

    removeContract(index: number) {
        if (this.schedule.contracts.length > index) {
            this.schedule.contracts.splice(index, 1);
        }
        return false;
    }

    validateLocCode() {
        this.formState.loc = new ErrorState();
        if (this.formState.submitted) {
            if (!this.schedule.locCode || this.schedule.locCode.length == 0) {
                this.formState.loc.invalid = true;
                this.formState.loc.errorMessage = 'Loc is required';
            }
        }
    }

    validateCommodity() {
        this.formState.commodity = new ErrorState();
        if (this.formState.submitted) {
            if (this.schedule.alternateCommodity.length == 0) {
                if (!this.schedule.commodityCode || this.schedule.commodityCode.length == 0) {
                    this.formState.commodity.invalid = true;
                    this.formState.commodity.errorMessage = 'Commodity or Alternate Commodity is required';
                }
            }
        }
    }

    validatePeriodDates() {
        if (!this.formState.submitted) {
            return;
        }

        this.formState.scheduleBeginDate = new ErrorState();
        this.formState.scheduleEndDate = new ErrorState();

        if (!this.schedule.scheduleBeginDate || this.schedule.scheduleBeginDate.length == 0) {
            this.formState.scheduleBeginDate.invalid = true;
            this.formState.scheduleBeginDate.errorMessage = 'Schedule Start Date is required.';
        }
        if (!this.schedule.scheduleEndDate || this.schedule.scheduleEndDate.length == 0) {
            this.formState.scheduleEndDate.invalid = true;
            this.formState.scheduleEndDate.errorMessage = 'Schedule End Date is required.';
        }

        if (!this.formState.scheduleBeginDate.invalid && !this.formState.scheduleEndDate.invalid) {
            let beginDate: Date = new Date(this.schedule.scheduleEndDate);
            let endDate: Date = new Date(this.schedule.scheduleEndDate);
            if (!isFinite(+beginDate)) {
                this.formState.scheduleBeginDate.invalid = true;
                this.formState.scheduleBeginDate.errorMessage = 'Schedule Start Date must be a valid date.';
            }
            if (!isFinite(+endDate)) {
                this.formState.scheduleEndDate.invalid = true;
                this.formState.scheduleEndDate.errorMessage = 'Schedule End Date must be a valid date.';
            }
            if (!this.formState.scheduleBeginDate.invalid && !this.formState.scheduleEndDate.invalid) {
                if (this.schedule.scheduleEndDate < this.schedule.scheduleBeginDate) {
                    this.formState.scheduleEndDate.invalid = true;
                    this.formState.scheduleEndDate.errorMessage = 'The Schedule End Date must be after the Schedule Start Date';
                }
            }
        }
    }

    validateLocation() {
        this.formState.location = new ErrorState();
        if (this.formState.submitted) {
            if (!this.schedule.location.locationId || this.schedule.location.locationId == 0) {
                this.formState.location.invalid = true;
                if (this.scheduleDefaults.locationType == 0) {
                    this.formState.location.errorMessage = 'Origin is required';
                }
                else {
                    this.formState.location.errorMessage = 'Destination is required';
                }
            }
        }
    }

    validateForm() {
        this.validateLocCode();
        this.validateCommodity();
        this.validatePeriodDates();
        this.validateLocation();
    }

    onSaveClick() {
        this.saveClick.next();
    }

    saveSchedule(): void {
        this.formState.submitted = true;
        this.validateForm();

        if (this.formState.isValid()) {
            this.formStatus = FormStatus.Saving;
            this._scheduleDataService.saveSchedule(this.schedule).subscribe({
                next: data => {
                    this.saveComplete.emit(data);
                    this.closeButton.nativeElement.click();
                },
                error: err => {
                    this.errorMessage = err;
                    this.formStatus = FormStatus.Normal;
                }
            });
        }
    }

    deleteSchedule(): void {
        this.formStatus = FormStatus.Delete;
    }

    cancelDelete(): void {
        this.formStatus = FormStatus.Normal;
    }

    confirmDelete(): void {
        this.formStatus = FormStatus.Deleting;
        this._scheduleDataService.deleteSchedule(this.schedule.scheduleId).subscribe({
            next: data => {
                this.deleteComplete.emit(this.schedule.scheduleId);
                this.closeButton.nativeElement.click();
            },
            error: err => {
                this.errorMessage = err;
                this.formStatus = FormStatus.Normal;
            }
        });
    }

    setLocationTypeNames() {
        if (this.schedule.locationType == LocationType.Origin) {
            this.locationTypeName = 'Origin';
            this.loadType = 'Load';
        }
        else {
            this.locationTypeName = 'Destination';
            this.loadType = 'Unload';
        }
    }

    getSchedule(): void {
        if (this.scheduleDefaults.scheduleId > 0) {
            this._scheduleDataService.getSchedule(this.scheduleDefaults.scheduleId).subscribe({
                next: data => {
                    this.schedule = data;
                    this.setLocationTypeNames();
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            this.isNew = true;
            if (this.scheduleDefaults.locCode.startsWith('G-')) {
                this.isLocGroup = true;
                this.getGroupLocList(this.scheduleDefaults.locCode);
            }
            else {
                this.schedule.locCode = this.scheduleDefaults.locCode;
            }
            this.schedule.locationType = this.scheduleDefaults.locationType;
            this.schedule.scheduleBeginDate = this.scheduleDefaults.periodStartDate;
            this.schedule.scheduleEndDate = this.scheduleDefaults.periodEndDate;
            this.schedule.commodityCode = this.scheduleDefaults.commodityCode;
            this.schedule.scheduleType = "M";
            this.setLocationTypeNames();
        }
    }

    getGroupLocList(groupCode: string) {
        if (groupCode && groupCode.length > 0) {
            this._listDataService.getGroupLocs(groupCode).subscribe({
                next: data => {
                    this.locList = data;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    getCommodityList(locCode: string) {
        this.commodityList = [];
        if (locCode && locCode.length > 0) {
            this._listDataService.getCommodities(locCode).subscribe({
                next: data => {
                    this.commodityList = data;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    resetComponent() {
        this.formStatus = FormStatus.Normal;
        this.formState = new ScheduleFormState();
        this.schedule = new Schedule();

        this.errorMessage = '';
        this.locationTypeName = '';
        this.loadType = '';
        this.isNew = false;
        this.isLocGroup = false;

        this.locList = [];
        this.commodityList = [];
        this.contractList = [];
        this.clearLocationSearch();
        this.contractLoadsError = '';
        this.isShowNotes = false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['scheduleDefaults']) {
            this.resetComponent();
            this.getCommodityList(this.scheduleDefaults.locCode);
            this.getSchedule();
        }
    }

    ngOnInit(): void {
        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            this.isMobile = true;
        }

        //prevent double click from calling save twice
        this.saveClick.pipe(
            debounceTime(500)
        ).subscribe(() => this.saveSchedule());

    }

    showHideNotes() {
        this.isShowNotes = !this.isShowNotes;
    }

    preventNegativeKey(event: KeyboardEvent): void {
        if (event.key === '-' || event.key === 'e') {
            event.preventDefault();
        }
    }

    preventNegative(event: any): void {
        const value = event.target.value;
        event.target.value = value.replace(/[^0-9.]/g, '');
        this.schedule.loads = event.target.value;
    }

}
